/**
 * Client-side ZIP download. The backend has no bulk/zip endpoint (documents are
 * streamed one-by-one from local disk), so "download all" is assembled in the
 * browser: each app fetches its document blobs (authenticated) and hands them
 * here to be zipped and saved.
 *
 * JSZip is loaded via dynamic import so it only ships in the chunk for pages
 * that actually offer a bulk download.
 */

const MIME_EXT: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
  'text/plain': 'txt',
  'application/zip': 'zip',
};

/** Best-effort file extension for a MIME type (empty string when unknown). */
export function extensionForMime(mime: string | null | undefined): string {
  if (!mime) return '';
  return MIME_EXT[mime.toLowerCase()] ?? '';
}

/** Append `.ext` (derived from MIME) to `name` unless it already has that extension. */
export function ensureExtension(name: string, mime: string | null | undefined): string {
  const ext = extensionForMime(mime);
  if (!ext) return name;
  return name.toLowerCase().endsWith(`.${ext}`) ? name : `${name}.${ext}`;
}

export interface ZipEntry {
  /** Filename inside the archive (extension included where known). */
  name: string;
  blob: Blob;
}

function sanitize(name: string): string {
  return (name || 'document').replace(/[\\/:*?"<>|\r\n\t]+/g, '_').trim() || 'document';
}

/**
 * Zip the given entries and trigger a browser download. De-duplicates colliding
 * names (`file.pdf`, `file (1).pdf`, …). Rejects if no entries are supplied.
 */
export async function zipAndDownload(entries: ZipEntry[], zipName: string): Promise<void> {
  if (entries.length === 0) throw new Error('Nothing to download.');

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const counts = new Map<string, number>();

  for (const entry of entries) {
    const base = sanitize(entry.name);
    const seen = counts.get(base) ?? 0;
    counts.set(base, seen + 1);
    let name = base;
    if (seen > 0) {
      const dot = base.lastIndexOf('.');
      name = dot > 0 ? `${base.slice(0, dot)} (${seen})${base.slice(dot)}` : `${base} (${seen})`;
    }
    zip.file(name, entry.blob);
  }

  const content = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(content);
  const link = document.createElement('a');
  link.href = url;
  link.download = zipName.toLowerCase().endsWith('.zip') ? zipName : `${zipName}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
